import { Injectable, inject } from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  startWith,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { LogoutWarningComponent } from '../components';
import { LogoutWarningResolution } from '../../models/logout-warning.enum';
import { Models } from '../../models/global.models';
import { IntegratedLoginContext, SessionQuery } from '../state';
import { InteractionStatus } from '@azure/msal-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

@Injectable({
  providedIn: 'root',
})
export class LogoutWarningService {
  private readonly _dialogService = inject(DialogService);
  private readonly _environment = inject(APP_ENV);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _msalService = inject(MsalService);
  private readonly _sessionQuery = inject(SessionQuery);

  constructor() {
    // Create an observable that emits when MSAL is initialized
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v3-v4-upgrade-guide.md#using-local-storage
    this._msalInitialized$ = this._msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      map(() => true),
      startWith(false),
      takeUntilDestroyed(),
    );
  }

  private _secondsInactivity = 60 * 60;
  private _secondsWindowCountdown = 5 * 60;
  private _refreshEvent$?: Observable<number | Event>; // clicks and key presses
  private _logoutTimerExpired$?: Observable<boolean>; // Resets after every user interaction event
  private _logoutWarningRef: DynamicDialogRef; // logout warning modal
  private readonly _msalInitialized$: Observable<boolean>; // Observable for MSAL initialization status

  // Check for Last Activity Date changes from other tabs and close warning dialog if counting down
  private _lastActivityDateModified$: Observable<StorageEvent> = fromEvent<StorageEvent>(window, 'storage').pipe(
    filter((event) => event.storageArea === localStorage && event.key === Models.LocalStorage.LastActivityDate),
    debounceTime(0),
    tap(() => {
      if (this._logoutWarningRef) {
        this._logoutWarningRef.close();
      }
    }),
  );

  get _lastActivityDate(): Date {
    return new Date(localStorage.getItem(Models.LocalStorage.LastActivityDate));
  }

  set _lastActivityDate(activityDate: Date) {
    localStorage.setItem(Models.LocalStorage.LastActivityDate, activityDate.toString());
  }

  get _expiredDate(): Date {
    return new Date(this._lastActivityDate.getTime() + this._secondsInactivity * 1000 + this._secondsWindowCountdown * 1000);
  }

  get _isExpired(): boolean {
    return this._expiredDate < new Date();
  }

  get _isWarning(): boolean {
    return new Date(this._lastActivityDate.getTime() + this._secondsInactivity * 1000) < new Date();
  }

  /**
   * Starts timer for user inactivity and will open LogoutWarningComponent after threshold
   * Use environment.logoutWarning for config
   * Note: This will set _authService.isUserLoggedIn$ to false. Use onLogout for additional logic.
   * @param onLogout - Callback method for additional logout logic
   */
  initLogoutWarningTimer(onLogout: () => any) {
    if (!this._environment?.logoutWarning?.isActive) {
      return;
    } else {
      this._lastActivityDate = new Date();
      this._sessionQuery
        .select((s) => s.integratedLoginContext)
        .pipe(take(1))
        .subscribe((integratedLoginContext) => {
          if (integratedLoginContext === IntegratedLoginContext.Byte) {
            this._secondsInactivity = 2 * 60 * 60;
          } else {
            this._secondsInactivity = this._environment?.logoutWarning?.secondsInactivity ?? this._secondsInactivity;
          }
        });
      this._secondsWindowCountdown = this._environment?.logoutWarning?.secondsWindowCountdown ?? this._secondsWindowCountdown;
    }

    const loginExpired = () => {
      if (this._isExpired) {
        onLogout();
        if (this._logoutWarningRef) {
          this._logoutWarningRef.close(LogoutWarningResolution.Logout);
        }
      }
    };

    // Start subs to DOM events and activity from other Clear2 tabs
    this._refreshEvent$ = merge(fromEvent(document, 'keypress'), fromEvent(document, 'click'), this._lastActivityDateModified$).pipe(
      throttleTime(1000), // Throttle to every one second
      filter(() => !this._isExpired),
      tap(() => {
        if (!this._isExpired) {
          this._lastActivityDate = new Date();
        }
      }),
      startWith(0),
    );

    this._logoutTimerExpired$ = this._refreshEvent$.pipe(
      // First, switch to the MSAL initialization observable
      switchMap(() => this._msalInitialized$),
      // Only continue when MSAL is initialized
      filter((isInitialized) => isInitialized),
      // Then continue with the timer logic
      switchMap(() => interval(1000)),
      filter(() => this._msalService.instance.getAllAccounts().length > 0),
      map((val) => {
        if (this._isExpired) {
          loginExpired();
        }
        return (val > this._secondsInactivity || this._isWarning) && !this._isExpired ? true : false;
      }),
      startWith(false),
      distinctUntilChanged(),
    );

    // Open modal after inactivity if modal not open already
    this._logoutTimerExpired$.pipe(filter((expired) => expired)).subscribe(() => {
      this._logoutWarningRef = this._dialogService.open(LogoutWarningComponent, {
        header: 'Session Expiring',
        width: '400px',
        style: { 'max-width': 'calc(100vw - 20px)' },
        styleClass: 'logout-warning',
        data: { totalTimeSeconds: this._environment?.logoutWarning?.secondsWindowCountdown },
      });
      this._logoutWarningRef.onClose.subscribe((resolution: LogoutWarningResolution) => {
        this._logoutWarningRef = null;
        if (resolution === LogoutWarningResolution.Logout) {
          onLogout();
        } else {
          this._lastActivityDate = new Date();
        }
      });
    });
  }
}
